import React from 'react';
import { ChevronLeft, ChevronRight, Clock, Users } from 'lucide-react';

function WeeklyCalloutsListView({
  families,
  allContent,
  relatives,
  onBack,
  onSelectCallout,
}) {
  const getNameForUuid = (uuid) => {
    const rel = relatives.find(r => r.uuid === uuid);
    return rel?.name || null;
  };

  // Current callouts from families
  const currentCallouts = (families || [])
    .filter(f => f.weeklyCallout && f.weeklyCallout.question)
    .map(f => {
      const callout = f.weeklyCallout;
      const answered = callout.answeredUserUuids || [];
      const waiting = callout.waitingOnUserUuids || [];
      return {
        ...callout,
        familyName: f.name,
        familyUuid: f.uuid,
        answeredNames: answered.map(getNameForUuid).filter(Boolean),
        waitingNames: waiting.map(getNameForUuid).filter(Boolean),
        answeredCount: answered.length,
        totalCount: answered.length + waiting.length,
        isCurrent: true,
      };
    });

  // Past callouts derived from content responses
  const currentQuestionUuids = new Set(currentCallouts.map(c => c.questionUuid));
  const pastCalloutMap = new Map();

  (allContent || []).forEach(content => {
    if (content.question?.questionType !== 'weeklyCallout') return;
    const qUuid = content.question.questionUuid;
    if (!qUuid || currentQuestionUuids.has(qUuid)) return;

    if (!pastCalloutMap.has(qUuid)) {
      pastCalloutMap.set(qUuid, {
        questionUuid: qUuid,
        question: content.question.question,
        summary: content.question.questionSummary || content.question.question,
        responseCount: 0,
        latestDate: null,
        isCurrent: false,
      });
    }

    const entry = pastCalloutMap.get(qUuid);
    entry.responseCount += 1;
    const contentDate = content.creation ? new Date(content.creation) : null;
    if (contentDate && (!entry.latestDate || contentDate > entry.latestDate)) {
      entry.latestDate = contentDate;
    }
  });

  const pastCallouts = Array.from(pastCalloutMap.values())
    .sort((a, b) => (b.latestDate || 0) - (a.latestDate || 0));

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center gap-3 sticky top-0 z-40 shadow-xl">
        <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Weekly Callouts</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Current Callouts */}
        {currentCallouts.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-purple-400 mb-3 px-1">This Week</h2>
            <div className="space-y-3">
              {currentCallouts.map((callout, idx) => (
                <div
                  key={`current-${idx}`}
                  onClick={() => onSelectCallout(callout)}
                  className="bg-purple-600/15 border border-purple-500/30 rounded-2xl p-4 cursor-pointer hover:bg-purple-600/25 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-purple-300 text-xs font-medium mb-1">{callout.familyName}</p>
                      <p className="text-white font-semibold text-sm">{callout.summary || callout.question}</p>

                      <div className="mt-2 flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-purple-400" />
                        <span className="text-purple-300 text-xs">
                          {callout.answeredCount} of {callout.totalCount} answered
                        </span>
                      </div>

                      {callout.answeredNames.length > 0 && (
                        <p className="text-gray-400 text-[11px] mt-1 truncate">
                          {callout.answeredNames.join(', ')}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Callouts */}
        {pastCallouts.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 mb-3 px-1">Previous</h2>
            <div className="space-y-2">
              {pastCallouts.map((callout) => (
                <div
                  key={callout.questionUuid}
                  onClick={() => onSelectCallout(callout)}
                  className="bg-gray-800 rounded-xl p-4 cursor-pointer hover:bg-gray-750 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{callout.summary || callout.question}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        {callout.latestDate && (
                          <span className="text-gray-500 text-xs">{formatDate(callout.latestDate)}</span>
                        )}
                        <span className="text-gray-500 text-xs">
                          {callout.responseCount} {callout.responseCount === 1 ? 'response' : 'responses'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {currentCallouts.length === 0 && pastCallouts.length === 0 && (
          <div className="text-center py-16">
            <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No weekly callouts yet</p>
            <p className="text-sm text-gray-500 mt-2">Callouts will appear here as they are created</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default WeeklyCalloutsListView;
